import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useMentionRoster } from './mentionRoster';
import './mentionSuggest.css';

type Field = HTMLInputElement | HTMLTextAreaElement;

/** The @name the caret is inside: where the @ sits, where the name ends, and the letters typed so far, lowercased. */
export interface MentionToken {
  start: number;
  end: number;
  query: string;
}

const HANDLE_CHAR = /[A-Za-z0-9._-]/;
/** A roster entry the regex above could ever match; one with a space in it can never be mentioned, so it is never offered. */
const HANDLE = /^[A-Za-z0-9._-]+$/;

/**
 * The @name under the caret, or null when the caret is not in one. An @ glued to the word before it (an e-mail
 * address, "3@1000") is not a mention, so it never opens the list.
 */
export function mentionTokenAt(text: string, caret: number | null): MentionToken | null {
  if (caret === null || caret < 0 || caret > text.length) {
    return null;
  }
  let from = caret;
  while (from > 0 && HANDLE_CHAR.test(text.charAt(from - 1))) {
    from -= 1;
  }
  if (from === 0 || text.charAt(from - 1) !== '@') {
    return null;
  }
  const start = from - 1;
  if (start > 0 && !/\s/.test(text.charAt(start - 1))) {
    return null;
  }
  let end = caret;
  while (end < text.length && HANDLE_CHAR.test(text.charAt(end))) {
    end += 1;
  }
  return { start, end, query: text.slice(from, caret).toLowerCase() };
}

/** Up to `limit` handles for what was typed: those that start with it first, then those that contain it, A to Z. */
export function rankHandles(roster: Iterable<string>, query: string, limit = 8): string[] {
  const all = [...roster].filter((handle) => HANDLE.test(handle)).sort((a, b) => a.localeCompare(b));
  const starts = all.filter((handle) => handle.startsWith(query));
  const contains = query === '' ? [] : all.filter((handle) => !handle.startsWith(query) && handle.includes(query));
  return [...starts, ...contains].slice(0, limit);
}

export interface MentionSuggestOptions {
  /** The draft as the composer holds it. */
  value: string;
  /** How the composer takes a new draft once a name is picked. */
  onChange: (next: string) => void;
}

/** Handlers take the field union so the same hook fits a plain input, a textarea and react-bootstrap's Form.Control. */
export interface MentionSuggestFieldProps<T extends Field> {
  ref: RefObject<T>;
  onFocus: () => void;
  onBlur: () => void;
  onClick: (event: SyntheticEvent<Field>) => void;
  onKeyUp: (event: SyntheticEvent<Field>) => void;
  onSelect: (event: SyntheticEvent<Field>) => void;
  'aria-autocomplete': 'list';
  'aria-haspopup': 'listbox';
  'aria-controls'?: string | undefined;
  'aria-activedescendant'?: string | undefined;
}

export interface MentionSuggest<T extends Field> {
  /** Spread onto the input or textarea, before its own props. */
  fieldProps: MentionSuggestFieldProps<T>;
  /** Call first in the field's own onKeyDown; true means the list took the key (a pick, a move, a dismissal). */
  onKeyDown: (event: KeyboardEvent<Field>) => boolean;
  /** Render once anywhere in the composer: the list portals to the document body and sits under the field. */
  list: ReactNode;
  /** Whether the list is showing. */
  open: boolean;
  /** The @name under the caret while the list is open, so the unknown-name warning can hold off until it is finished. */
  typing: string | null;
}

/** A key pressed while an IME is still composing (Chrome on Windows says so only through keyCode 229). */
export const isComposingKey = (event: KeyboardEvent<Field>): boolean =>
  event.nativeEvent.isComposing || event.keyCode === 229;

const LIMIT = 8;
/** Eight rows and the key hint, the most the list ever needs. */
const LIST_HEIGHT = 320;
/** The typed letters in bold inside the handle, so the eye sees why each name is offered. */
function highlight(handle: string, query: string): ReactNode {
  const at = query === '' ? -1 : handle.indexOf(query);
  if (at < 0) {
    return handle;
  }
  return (
    <>
      {handle.slice(0, at)}
      <b>{handle.slice(at, at + query.length)}</b>
      {handle.slice(at + query.length)}
    </>
  );
}

/**
 * The names a composer can offer when the operator types @ (SSD-7435): the same roster the unknown-name warning
 * checks against, shown as a list under the field, picked with the arrow keys and Enter or with the mouse. The field
 * stays a plain input: the composer keeps its own value, change handler and Enter-to-post, and only asks the hook
 * first whether a key belongs to the list.
 */
export function useMentionSuggest<T extends Field>({ value, onChange }: MentionSuggestOptions): MentionSuggest<T> {
  const ref = useRef<T>(null);
  const listId = useId();
  const roster = useMentionRoster();
  const [caret, setCaret] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<{ start: number; query: string } | null>(null);
  const [box, setBox] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);
  const pendingCaret = useRef<number | null>(null);

  // After the text changes the caret is wherever the browser left it, unless a pick asked for a particular spot.
  useLayoutEffect(() => {
    const field = ref.current;
    if (!field) {
      return;
    }
    const wanted = pendingCaret.current;
    if (wanted !== null) {
      pendingCaret.current = null;
      field.setSelectionRange(wanted, wanted);
      setCaret(wanted);
      return;
    }
    setCaret(field.selectionStart);
  }, [value]);

  const token = useMemo(() => mentionTokenAt(value, caret), [value, caret]);
  const options = useMemo(() => (token && roster ? rankHandles(roster, token.query, LIMIT) : []), [token, roster]);
  const open =
    focused &&
    token !== null &&
    options.length > 0 &&
    !(dismissed !== null && dismissed.start === token.start && dismissed.query === token.query);

  const tokenStart = token?.start;
  const tokenQuery = token?.query;
  useEffect(() => {
    setActive(0);
  }, [tokenStart, tokenQuery]);

  // The list sits under the field and follows it while the page or the table scrolls.
  useLayoutEffect(() => {
    if (!open) {
      setBox(null);
      return undefined;
    }
    const place = () => {
      const field = ref.current;
      if (!field) {
        return;
      }
      const rect = field.getBoundingClientRect();
      const width = Math.max(220, Math.min(rect.width, 360));
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      const below = window.innerHeight - rect.bottom;
      // Opens upward only when the room under the field is short and the room above it is longer.
      if (below < LIST_HEIGHT && rect.top > below) {
        setBox({ left, width, bottom: window.innerHeight - rect.top + 4 });
      } else {
        setBox({ left, width, top: rect.bottom + 4 });
      }
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const pick = useCallback(
    (handle: string) => {
      if (!token) {
        return;
      }
      const after = value.slice(token.end);
      // A space already after the name is reused, so a pick in the middle of a sentence never doubles it.
      const gap = /^\s/.test(after) ? '' : ' ';
      const next = `${value.slice(0, token.start)}@${handle}${gap}${after}`;
      pendingCaret.current = token.start + handle.length + 2;
      setDismissed(null);
      onChange(next);
      ref.current?.focus();
    },
    [token, value, onChange],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<Field>): boolean => {
      // A key pressed while an IME is still composing belongs to the composition, never to the list.
      if (!open || isComposingKey(event)) {
        return false;
      }
      switch (event.key) {
        case 'ArrowDown':
          setActive((index) => (index + 1) % options.length);
          break;
        case 'ArrowUp':
          setActive((index) => (index - 1 + options.length) % options.length);
          break;
        case 'Enter':
        case 'Tab': {
          const handle = options[active] ?? options[0];
          if (handle) {
            pick(handle);
          }
          break;
        }
        case 'Escape':
          setDismissed(token ? { start: token.start, query: token.query } : null);
          break;
        default:
          return false;
      }
      event.preventDefault();
      return true;
    },
    [open, options, active, pick, token],
  );

  const sync = useCallback((event: SyntheticEvent<Field>) => {
    setCaret(event.currentTarget.selectionStart);
  }, []);

  const fieldProps: MentionSuggestFieldProps<T> = {
    ref,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    onClick: sync,
    onKeyUp: sync,
    onSelect: sync,
    'aria-autocomplete': 'list',
    'aria-haspopup': 'listbox',
    'aria-controls': open ? listId : undefined,
    'aria-activedescendant': open ? `${listId}-${active}` : undefined,
  };

  const list =
    open && box
      ? createPortal(
          <div
            className="mention-suggest"
            data-testid="mention-suggest"
            style={{ top: box.top, bottom: box.bottom, left: box.left, width: box.width }}
          >
            <ul role="listbox" id={listId} aria-label="People to mention">
              {options.map((handle, index) => (
                <li
                  key={handle}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  className={index === active ? 'on' : undefined}
                  onMouseDown={(event) => {
                    // The press must not blur the field, or the list closes before the pick lands.
                    event.preventDefault();
                    pick(handle);
                  }}
                  onMouseEnter={() => setActive(index)}
                >
                  <span className="mention-avatar" aria-hidden="true">
                    {handle.charAt(0)}
                  </span>
                  <span className="mention-handle">@{highlight(handle, token?.query ?? '')}</span>
                </li>
              ))}
            </ul>
            <div className="mention-keys" aria-hidden="true">
              ↑↓ move · Enter picks · Esc hides
            </div>
          </div>,
          document.body,
        )
      : null;

  const typing = open && token ? value.slice(token.start + 1, token.end).toLowerCase() : null;

  return { fieldProps, onKeyDown, list, open, typing };
}
